import { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, Star } from 'lucide-react';
import { useT } from '../i18n/I18nProvider';
import { useNavigation } from '../navigation/useNavigation';
import { useBadges, type BadgeSeverity, type BadgeValue } from '../navigation/badges';
import { toggleFavorite, useFavorites } from '../hooks/useFavorites';
import type { NavItem } from '../navigation/registry';

type T = (key: string) => string;
type IsActive = (route: string) => boolean;
type Badges = Record<string, BadgeValue | undefined>;

function useIsActive(): IsActive {
  const { pathname } = useLocation();
  return (route: string): boolean =>
    route === '/' ? pathname === '/' : pathname === route || pathname.startsWith(`${route}/`);
}

const SEVERITY_RANK: Record<BadgeSeverity, number> = { info: 1, warning: 2, critical: 3 };

/** Combine the badges of a group's items into one aggregate for the collapsed header. */
function aggregate(items: NavItem[], badges: Badges): BadgeValue | undefined {
  let count = 0;
  let severity: BadgeSeverity | undefined;
  for (const item of items) {
    const b = item.badge ? badges[item.badge] : undefined;
    if (b) {
      count += b.count;
      if (!severity || SEVERITY_RANK[b.severity] > SEVERITY_RANK[severity]) severity = b.severity;
    }
  }
  return count > 0 && severity ? { count, severity } : undefined;
}

function NavBadge({ value, label }: { value: BadgeValue; label: string }): JSX.Element {
  return (
    <span
      className={`nav-badge nav-badge--${value.severity}`}
      aria-label={`${value.count} ${label}`}
    >
      {value.count > 99 ? '99+' : value.count}
    </span>
  );
}

/** A leaf destination link (used for single-domain flat links and group children). */
function ItemLink({
  item,
  collapsed,
  flat,
  badge,
  favorite,
  t,
  onNavigate,
}: {
  item: NavItem;
  collapsed: boolean;
  flat: boolean;
  badge: BadgeValue | undefined;
  /** When set, this row is in the Favorites section and shows an unpin control. */
  favorite?: boolean;
  t: T;
  onNavigate?: () => void;
}): JSX.Element {
  const Icon = item.icon;
  const label = t(item.labelKey);
  return (
    <li className={favorite ? 'nav__row' : undefined}>
      <NavLink
        to={item.route}
        end={item.route === '/'}
        className={({ isActive }) =>
          `nav__link${flat ? ' nav__link--flat' : ''}${isActive ? ' active' : ''}`
        }
        onClick={onNavigate}
        {...(collapsed ? { 'aria-label': label, title: label } : {})}
      >
        <Icon className="nav-icon" size={flat ? 18 : 16} strokeWidth={2} aria-hidden="true" />
        {!collapsed && <span>{label}</span>}
        {badge && item.badge && <NavBadge value={badge} label={t(`nav.badges.${item.badge}`)} />}
      </NavLink>
      {favorite && !collapsed && (
        <button
          type="button"
          className="nav__fav is-fav"
          onClick={() => toggleFavorite(item.id)}
          aria-label={t('nav.unpin')}
          aria-pressed
        >
          <Star size={14} fill="currentColor" aria-hidden="true" />
        </button>
      )}
    </li>
  );
}

/**
 * Primary navigation, rendered from the typed registry.
 *
 * Single-destination domains render as a flat link; multi-destination domains render
 * as a collapsible group. One group is open at a time (accordion); the group holding
 * the current route opens automatically. Collapsed mode shows an icon rail with
 * tooltips. Attention badges (Phase 2.6) appear on leaf items and aggregate onto a
 * group header when the group is closed or the rail is collapsed.
 */
export function NavSidebar({
  collapsed,
  onExpand,
  onNavigate,
}: {
  collapsed: boolean;
  onExpand: () => void;
  onNavigate?: () => void;
}): JSX.Element {
  const t = useT();
  const navigate = useNavigate();
  const domains = useNavigation();
  const badges = useBadges();
  const favoriteIds = useFavorites();
  const isActive = useIsActive();

  // Resolve favorites against the *visible* items, so a pinned page the user can no
  // longer access (permission/mode) simply drops out of the section.
  const visibleById = new Map(domains.flatMap((d) => d.items).map((i) => [i.id, i]));
  const favItems = favoriteIds
    .map((id) => visibleById.get(id))
    .filter((i): i is NavItem => Boolean(i));
  const activeDomainId =
    domains.find(
      (d) =>
        d.items.some((i) => isActive(i.route)) ||
        (d.landingRoute ? isActive(d.landingRoute) : false),
    )?.id ?? null;
  const [openId, setOpenId] = useState<string | null>(activeDomainId);
  useEffect(() => {
    if (activeDomainId) setOpenId(activeDomainId);
  }, [activeDomainId]);

  return (
    <nav aria-label="Primary" className={`nav${collapsed ? ' nav--collapsed' : ''}`}>
      <ul className="nav__list">
        {!collapsed && favItems.length > 0 && (
          <li className="nav__group nav__favorites">
            <div className="nav__section-header">{t('nav.favorites')}</div>
            <ul className="nav__sublist">
              {favItems.map((item) => (
                <ItemLink
                  key={`fav-${item.id}`}
                  item={item}
                  collapsed={false}
                  flat={false}
                  badge={undefined}
                  favorite
                  t={t}
                  onNavigate={onNavigate}
                />
              ))}
            </ul>
          </li>
        )}
        {domains.map((domain) => {
          if (domain.items.length === 1) {
            // A single-destination domain with a landing page shows the landing
            // (labelled by the domain); the one sub-page is reached from there.
            const only = domain.items[0]!;
            const leaf: NavItem = domain.landingRoute
              ? {
                  id: domain.id,
                  labelKey: domain.labelKey,
                  route: domain.landingRoute,
                  icon: domain.icon,
                }
              : only;
            return (
              <ItemLink
                key={domain.id}
                item={leaf}
                collapsed={collapsed}
                flat
                badge={only.badge ? badges[only.badge] : undefined}
                t={t}
                onNavigate={onNavigate}
              />
            );
          }
          const open = !collapsed && openId === domain.id;
          const hasActiveChild =
            domain.items.some((i) => isActive(i.route)) ||
            (domain.landingRoute ? isActive(domain.landingRoute) : false);
          const groupBadge = aggregate(domain.items, badges);
          const Icon = domain.icon;
          const label = t(domain.labelKey);
          return (
            <li key={domain.id} className="nav__group">
              <button
                type="button"
                className={`nav__group-header${hasActiveChild ? ' has-active' : ''}`}
                aria-expanded={open}
                {...(collapsed ? { 'aria-label': label, title: label } : {})}
                onClick={() => {
                  if (collapsed) {
                    onExpand();
                    setOpenId(domain.id);
                    if (domain.landingRoute) navigate(domain.landingRoute);
                    return;
                  }
                  if (domain.landingRoute) {
                    // A domain with a landing page: go there and open the group.
                    // If we're already on it and open, a second click collapses.
                    if (open && isActive(domain.landingRoute)) setOpenId(null);
                    else {
                      navigate(domain.landingRoute);
                      setOpenId(domain.id);
                    }
                  } else {
                    setOpenId((cur) => (cur === domain.id ? null : domain.id));
                  }
                }}
              >
                <Icon className="nav-icon" size={18} strokeWidth={2} aria-hidden="true" />
                {!collapsed && <span className="nav__group-label">{label}</span>}
                {/* Aggregate badge when the group's contents are hidden (collapsed or closed). */}
                {groupBadge && (collapsed || !open) && (
                  <NavBadge value={groupBadge} label={t('nav.badges.attention')} />
                )}
                {!collapsed && (
                  <ChevronDown
                    className={`nav__chevron${open ? ' is-open' : ''}`}
                    size={15}
                    aria-hidden="true"
                  />
                )}
              </button>
              {open && (
                <ul className="nav__sublist">
                  {domain.items.map((item) => (
                    <ItemLink
                      key={item.id}
                      item={item}
                      collapsed={false}
                      flat={false}
                      badge={item.badge ? badges[item.badge] : undefined}
                      t={t}
                      onNavigate={onNavigate}
                    />
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
